import { TWT_REEL_RAW, TWT_REEL_VOICEOVER } from '~/tenants/twt/performable/reel/TwtReelJob';
import { TWT_VIDEO_VIRTUAL_STAGING } from '~/tenants/twt/performable/video/TwtVideoJob';
import React, { useEffect } from 'react';
import { TenantPerformableFormProps } from '~/tenants/common/form';
import { TwtOrderContext } from '~/tenants/twt/model/TwtOrder';
import Sections from '~/components/layout/Sections';
import { TenantPackageAddOnCheckboxName, TenantPackageAddOnTemplate } from '~/tenants/common/package';
import { PersonaType } from '~/tenants/common/TenantPersona';
import PerformableGroupForm, {
  useIsPerformableGroup,
  usePerformableGroup,
} from '~/components/performable/PerformableGroupForm';
import TwtAerialConfig, { TwtAerialType } from '~/tenants/twt/performable/aerial/TwtAerialConfig';
import ZodFieldHidden from '~/components/zod/ZodFieldHidden';
import TwtAerialAddOns from '~/tenants/twt/performable/aerial/TwtAerialAddOns';
import { TwtOrderType } from '~/tenants/twt/model/enums';
import { TenantPerformableAddOns } from '~/tenants/common/performable';
import InputCheckbox from '~/components/input/InputCheckbox';

export default function TwtVideoForm({ persona, context, create }: TenantPerformableFormProps<TwtOrderContext>) {
  const grouped = useIsPerformableGroup();

  return (
    <Sections>
      <TenantPackageAddOnCheckboxName
        name="voiceover"
        persona={persona}
        image="tenant/twt/cameo/thumbnail.jpeg"
        title="Agent Cameo / Voiceover"
        cost={TWT_REEL_VOICEOVER}
        description="Add a personal touch to your video with an on screen cameo or a descriptive voiceover."
      />
      {persona !== PersonaType.CUSTOMER && (
        <TenantPackageAddOnCheckboxName
          name="raw"
          persona={persona}
          image="tenant/twt/reel/thumbnail.png"
          title="Raw Footage"
          cost={TWT_REEL_RAW}
          description="We will deliver the unedited video files used to build the video."
        />
      )}
      <TenantPackageAddOnCheckboxName
        name="virtual"
        persona={persona}
        image="tenant/twt/staging/after2.jpg"
        title="Video Virtual Staging"
        cost={TWT_VIDEO_VIRTUAL_STAGING}
        description="Have your video virtually staged with this AI-generated add-on."
      />
      {grouped && <Aerial context={context} persona={persona} create={create} />}
    </Sections>
  );
}

function Aerial({ persona, context, create }: TenantPerformableFormProps<TwtOrderContext>) {
  const group = usePerformableGroup();
  const added = group.hasPerformable(TwtAerialConfig);

  useEffect(() => {
    if (!group.hasPerformable(TwtAerialConfig)) {
      group.addPerformable(TwtAerialConfig, {
        type: TwtAerialType.VIDEO,
      });
    }
  }, []);

  return (
    <>
      {!create && !added && (
      <TenantPackageAddOnTemplate
        htmlFor="aerial"
        title="Aerial Video"
        description={TwtAerialConfig.short}
        image="tenant/twt/aerial/mini.jpg"
        cost="$70.00"
        persona={persona}
        enable={
          <InputCheckbox
            id="aerial"
            checked={added}
            onChange={() => {
              if (added) {
                group.removePerformable(TwtAerialConfig);
              } else {
                group.addPerformable(TwtAerialConfig);
              }
            }}
          />
        }
      />)}
      {added && (
        <PerformableGroupForm performable={TwtAerialConfig}>
          <ZodFieldHidden name="type" value={TwtAerialType.VIDEO} />

          <TenantPerformableAddOns persona={persona}>
            <TwtAerialAddOns persona={persona} commercial={context.metadata.type === TwtOrderType.COMMERCIAL} />
          </TenantPerformableAddOns>
        </PerformableGroupForm>
      )}
    </>
  );
}
